import { UserAssessment, useUserAssessmentHooks } from '@api/assessmentQuestionnaireApiHandler';
import { getIpAddress } from '@api/auth/login';
import { engagementApiHandler } from '@api/engagementApiHandler';
import { logActionAlertEmail } from '@api/notifications';
import { thoughtsAndSpeakUpsApiHandler } from '@api/thoughtsAndSpeakUpsApiHandler';
import Seo from '@components/SEO';
import ThemeContextProvider from '@context/ThemeContext';
import { QuestionnaireType } from '@enums/QuestionnaireTypes';
import { useAppDispatch } from '@hooks/useAppDispatch';
import { useAppSelector } from '@hooks/useAppSelector';
import useGoNative from '@hooks/useGoNative';
import useWindowDimensions from '@hooks/useWindowDimensions';
import { ApiResponse } from '@interfaces/index';
import { ToastProvider, showToast } from '@shared/atoms';
import {
  CheckIn,
  DesktopFooter,
  Header,
  MobileFooter,
  SecondaryNavigation,
  SideNavigationBar,
} from '@shared/organisms';
import NotificationComponent from '@shared/organisms/CoreLayout/Header/components/NotificationComponent';
import { useIsFetching, useQuery, useQueryClient } from '@tanstack/react-query';
import { LogTypes, TwitterCardType } from 'custom.d';
import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { ProSidebarProvider } from 'react-pro-sidebar';
import { useHistory, useLocation } from 'react-router';
import { RootState } from 'store';
import { configurationSliceActions } from 'store/Slices/configurationsSlice';
import styles from './index.module.scss';

const getDayBasedMessage = () => {
  const dayMessages: Record<number, string> = {
    0: '🌟 Sunday reflection! Great way to prepare for the week ahead!',
    1: '💪 Monday momentum! Beginning the week with self-care!',
    2: '🚀 Tuesday growth! Your consistency is nurturing positive change!',
    3: '🎯 Wednesday wellness! Halfway through the week strong!',
    4: '⚡ Thursday thrive! Your commitment to wellbeing shines!',
    5: '🎉 Friday focus! Ending the week strong with self-care!',
    6: '🌈 Saturday strength! Weekend wellness in action!',
  };

  const today = new Date().getDay() as keyof typeof dayMessages;

  return dayMessages[today];
};

export const CoreLayout: React.FunctionComponent<CoreLayoutProps> = (props) => {
  const {
    children,
    title,
    description,
    name,
    type,
    secondaryNavEnabled,
    navigationType,
    bgColor,
    fullWidthEnabled,
  } = props;
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const userStatus = useAppSelector((state: RootState) => state.userStatus.data);
  const { isDesktop, isMobile, isTablet } = useWindowDimensions();
  const isMobileDevice = isMobile || isTablet;
  const urlParams = new URLSearchParams(window.location.search);
  const navigatedBy = urlParams.get('navigatedBy');
  const isMobileApp = useGoNative();
  const history = useHistory();
  const checkInParam = urlParams.get('check-in');
  const pathName = window.location.pathname;
  const [shortStarModalIsOpen, setShortStarModalIsOpen] = useState<boolean>(false);
  const location = useLocation();
  const [notificationOpen, setNotificationOpen] = useState(false);
  const isCheckInFetching =
    useIsFetching({
      queryKey: ['user-assessments-by-questionnaire-type', 'checkIn'],
    }) > 0;

  const checkInData: ApiResponse<UserAssessment[]> | undefined = queryClient.getQueryData([
    'user-assessments-by-questionnaire-type',
    'checkIn',
  ]);

  const { data: checkInAssessments, isLoading: isLoadingCheckIns } =
    useUserAssessmentHooks.useUserAssessmentsByQuestionnaireType(
      QuestionnaireType.CHECK_IN,
      Boolean(!checkInData),
    );

  const checkInLastAssessmentDate = useMemo(() => {
    const assessments =
      (checkInData?.success && checkInData?.responseObject) ||
      (checkInAssessments?.success && checkInAssessments?.responseObject) ||
      [];

    if (!Array.isArray(assessments) || assessments.length === 0) {
      return null;
    }

    const latestAssessment = assessments.reduce((latest, current) =>
      new Date(current.createdAt) > new Date(latest.createdAt) ? current : latest,
    );

    const formattedDate = latestAssessment?.createdAt
      ? new Date(latestAssessment.createdAt).toISOString().split('T')[0]
      : null;

    const today = new Date().toISOString().split('T')[0];

    if (formattedDate === today) {
      localStorage.setItem(
        'shortStarCacheHistory',
        JSON.stringify({ popupClosedToday: true, lastPopupDate: formattedDate }),
      );
    }

    return formattedDate;
  }, [checkInData, checkInAssessments]);

  useEffect(() => {
    if (navigatedBy && navigatedBy === 'alertEmail') {
      logAlertEmail();
    }
  }, [navigatedBy]);

  useEffect(() => {
    if (checkInParam === 'true') {
      setShortStarModalIsOpen(true);
    }
  }, [checkInParam]);

  useEffect(() => {
    if (
      userStatus?.isJoined &&
      userStatus?.isRegistered &&
      userStatus?.isAgreed &&
      userStatus?.isOnboardCompleted
    ) {
      handleDailyEngagementStreak();
    }
  }, [userStatus]);

  useEffect(() => {
    if (
      userStatus?.isOnboardCompleted &&
      checkInParam !== 'false' &&
      userStatus?.isJoined &&
      userStatus?.isRegistered &&
      userStatus?.isAgreed &&
      checkInParam !== 'false' &&
      !isCheckInFetching &&
      !isLoadingCheckIns
    ) {
      getValidatedCheckInOpen();
    }
  }, [userStatus, checkInLastAssessmentDate, isCheckInFetching, isLoadingCheckIns]);

  const {
    data: notificationsData,
    isLoading: notificationsLoading,
    isSuccess,
  } = useQuery({
    queryKey: ['thoughtsAndSpeakUpsForNotifications'],
    queryFn: () =>
      thoughtsAndSpeakUpsApiHandler.getThoughtsAndSpeakUps({
        archived: false,
        type: null,
      }),
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const latestNotification = useMemo(() => {
    if (notificationsData?.success && Array.isArray(notificationsData.responseObject)) {
      const withLatestResponse = notificationsData.responseObject.filter(
        (item): item is typeof item & { latestResponse: { createdAt: string } } =>
          item.latestResponse?.createdAt !== undefined,
      );

      if (withLatestResponse.length === 0) return null;

      const sorted = withLatestResponse.sort(
        (a, b) =>
          new Date(b.latestResponse.createdAt).getTime() -
          new Date(a.latestResponse.createdAt).getTime(),
      );

      return sorted[0];
    }

    return null;
  }, [notificationsData]);

  useEffect(() => {
    if (location.pathname === '/' && latestNotification) {
      setNotificationOpen(true);
    }
  }, [location.pathname, latestNotification]);

  const logAlertEmail = async () => {
    const ipAddress = await getIpAddress();
    const isNative = isMobileApp;
    const os = navigator.userAgentData?.platform || '';

    const res = await logActionAlertEmail(
      LogTypes.ACTION_ALERT_EMAIL_CLICKED,
      ipAddress,
      os,
      isNative,
    );
    if (res) {
      const url = new URL(window.location.href);
      const params = new URLSearchParams(url.search);

      params.delete('navigatedBy');
      const newUrl = `${url.pathname}?${params.toString()}`;
      history.replace(newUrl);
    }
  };

  const handleDailyEngagementStreak = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];

      let engagementData: EngagementStorageData = {};
      const storedEngagementData = localStorage.getItem('engagementStreakHistory');

      if (storedEngagementData) {
        engagementData = JSON.parse(storedEngagementData) as EngagementStorageData;
      }

      // Check if we haven't called the API today
      if (engagementData.lastStreakCallDate !== today) {
        const response = await engagementApiHandler.createEngagementUserStreak();

        if (response.success) {
          // Update localStorage with today's date
          engagementData.lastStreakCallDate = today;
          localStorage.setItem('engagementStreakHistory', JSON.stringify(engagementData));

          showToast(getDayBasedMessage(), undefined, 'success');
        }
      }
    } catch (error) {
      console.error('Error handling daily engagement streak:', error);
    }
  };

  const getValidatedCheckInOpen = async () => {
    const today = new Date().toISOString().split('T')[0];

    let localStorageData: LocalStorageData = {};
    const storedData = localStorage.getItem('shortStarCacheHistory');

    if (storedData) {
      localStorageData = JSON.parse(storedData) as LocalStorageData;
    }

    const lastAssessmentDate = checkInLastAssessmentDate;

    if (localStorageData && localStorageData.lastPopupDate !== today) {
      localStorageData.popupClosedToday = false;
    }

    if (checkInParam === 'true' && pathName === '/') {
      setShortStarModalIsOpen(true);
    }

    if (lastAssessmentDate === null && !localStorageData.popupClosedToday) {
      await setShortStarModalIsOpen(true);
      await dispatch(configurationSliceActions.forceShortStarModal(true));
      localStorageData.lastPopupDate = today;
    } else if (
      lastAssessmentDate &&
      lastAssessmentDate !== today &&
      !localStorageData.popupClosedToday
    ) {
      await setShortStarModalIsOpen(true);
      localStorageData.lastPopupDate = today;
    } else if (
      lastAssessmentDate !== today &&
      lastAssessmentDate !== null &&
      !localStorageData.popupClosedToday
    ) {
      await setShortStarModalIsOpen(true);
      localStorageData.lastPopupDate = today;
    } else if (!localStorageData.lastPopupDate || localStorageData.lastPopupDate !== today) {
      if (!localStorageData.popupClosedToday) {
        await setShortStarModalIsOpen(true);
        localStorageData.lastPopupDate = today;
      }
    }

    localStorage.setItem('shortStarCacheHistory', JSON.stringify(localStorageData));
  };

  const refetchCheckInAssessments = () => {
    // Invalidate specific queries
    queryClient.invalidateQueries({ queryKey: ['user-assessments-by-questionnaire-type'] });
    queryClient.invalidateQueries({ queryKey: ['fetchCurrentEngagement'] });
    queryClient.invalidateQueries({ queryKey: ['fetchUserStreak'] });
  };

  const handleNotificationClick = () => {
    setNotificationOpen(true);
  };

  const handleSnackbarClose = () => {
    setNotificationOpen(false);
  };

  const handleNotificationItemClick = async () => {
    if (latestNotification) {
      history.push('/shared-thoughts-and-speak-ups');
    }

    setNotificationOpen(false);
  };

  return (
    <ThemeContextProvider>
      <ProSidebarProvider>
        <Seo title={title} description={description} name={name} type={type} />
        <ToastProvider />
        <div className={styles.mainLayout} style={bgColor ? { background: bgColor } : {}}>
          <div className="sticky-top">
            {isDesktop && (
              <Header
                navigationType={navigationType}
                latestNotification={latestNotification}
                handleNotificationOpen={handleNotificationClick}
                notificationOpen={notificationOpen}
              />
            )}
            {secondaryNavEnabled && <SecondaryNavigation />}
          </div>
          <div className={fullWidthEnabled ? undefined : 'boxed'}>{children}</div>
          {shortStarModalIsOpen &&
            ReactDOM.createPortal(
              <CheckIn
                handleStep={() => null}
                modalOpen={shortStarModalIsOpen}
                handleModalOpen={setShortStarModalIsOpen}
                refetchCheckInAssessments={refetchCheckInAssessments}
              />,
              document.getElementById('short-star-modal')!,
            )}

          <SideNavigationBar />
          {isDesktop && <DesktopFooter />}
          {isMobileDevice && <MobileFooter navigationType={navigationType} />}

          <NotificationComponent
            open={notificationOpen}
            notification={latestNotification}
            onClose={handleSnackbarClose}
            onClick={handleNotificationItemClick}
            position={{ vertical: 'top', horizontal: isDesktop ? 'right' : 'center' }}
            showCloseButton
            clickable
            customColors={{
              background: '#0072b0',
              text: 'white',
              icon: 'white',
            }}
            noDataMessage={{
              title: 'All Caught Up!',
              subtitle: "You don't have any new messages at the moment.",
            }}
          />
        </div>
      </ProSidebarProvider>
    </ThemeContextProvider>
  );
};

CoreLayout.defaultProps = {
  description: '',
  name: '',
  type: 'summary',
  secondaryNavEnabled: false,
  bgColor: null,
  fullWidthEnabled: false,
};

interface LocalStorageData {
  lastPopupDate?: string;
  popupClosedToday?: boolean;
}

interface CoreLayoutProps {
  children: React.ReactNode | undefined;
  title: string;
  description?: string;
  name?: string;
  type?: TwitterCardType;
  secondaryNavEnabled?: boolean;
  navigationType: 'default' | 'admin' | 'superAdmin' | 'advisor';
  bgColor?: string | null;
  fullWidthEnabled?: boolean;
}

interface EngagementStorageData {
  lastStreakCallDate?: string;
}
